import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { RootState, AppThunk } from 'app/store';
import { AuthProvider as Provider } from 'app/firebase/collections/users';
import { UserModel } from 'app/firebase/collections/users';
import { User } from 'firebase/auth';
import * as Sentry from '@sentry/react';
import authAPI from 'app/auth';
import { localStorageAdminAccount, localStorageApiKey } from 'app/localstorage';
import { graphQLClient } from 'app/graphql/client';
import { checkoutIntentsClient } from 'app/checkout-intents/client';
import { trackHeapIdentifyEvent, trackSegmentIdentifyEvent } from 'app/utils';

import { getUser, updateAdminUser, updateAdminBrand } from 'app/graphql';
import { AuthProvider, BrandInput } from 'app/graphql/generated/admin/graphql';
import { toast } from 'sonner';

/**
 * Reducers
 */

type UserState = {
  loadingInfo: boolean;
  loadingJwtValidationKey: boolean;
  loadingMargin: boolean;
  loadingBrand: boolean;
  uid: string | null;
  info: UserModel | null;
  documentId: string | null;
  drawdownEnabled: boolean | null;
  billingInfoFetched: boolean;
  infoLoadFailed: boolean;
};

interface SetPayload {
  userInfo: UserModel;
  documentId: string | undefined;
}

const initialState: UserState = {
  loadingInfo: false,
  loadingJwtValidationKey: false,
  loadingMargin: false,
  loadingBrand: false,
  uid: null,
  info: null,
  documentId: null,
  drawdownEnabled: null,
  billingInfoFetched: false,
  infoLoadFailed: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clean: () => {
      return { ...initialState };
    },
    loadingInfo: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loadingInfo: action.payload,
        loadingJwtValidationKey: action.payload,
        loadingMargin: action.payload,
        loadingBrand: action.payload,
      };
    },
    loadingJwtValidationKey: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loadingJwtValidationKey: action.payload,
      };
    },
    loadingMargin: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loadingMargin: action.payload,
      };
    },
    loadingBrand: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loadingBrand: action.payload,
      };
    },
    login: (state, action: PayloadAction<{ uid: string }>) => {
      const accountChanged = state.uid !== action.payload.uid;
      return {
        ...state,
        info: accountChanged ? initialState.info : state.info,
        documentId: accountChanged ? initialState.documentId : state.documentId,
        infoLoadFailed: accountChanged ? initialState.infoLoadFailed : state.infoLoadFailed,
        loadingInfo: true,
        loadingJwtValidationKey: true,
        loadingMargin: true,
        loadingBrand: true,
        uid: action.payload.uid,
      };
    },
    set: (state, action: PayloadAction<SetPayload>) => {
      return {
        ...state,
        loadingInfo: false,
        loadingJwtValidationKey: false,
        loadingMargin: false,
        loadingBrand: false,
        info: action.payload.userInfo,
        documentId: action.payload.documentId || initialState.documentId,
        infoLoadFailed: false,
      };
    },
    infoLoadFailed: (state) => {
      return {
        ...state,
        loadingInfo: false,
        loadingJwtValidationKey: false,
        loadingMargin: false,
        loadingBrand: false,
        infoLoadFailed: true,
      };
    },
    update: (state, action) => {
      return {
        ...state,
        loadingInfo: false,
        loadingJwtValidationKey: false,
        loadingMargin: false,
        loadingBrand: false,
        info: {
          ...state.info,
          ...action.payload,
        },
      };
    },
    setDrawdownEnabled: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        drawdownEnabled: action.payload,
      };
    },
    setBillingInfoFetched: (state) => {
      return {
        ...state,
        billingInfoFetched: true,
      };
    },
  },
});

/**
 * Actions
 */
const {
  login,
  clean,
  set,
  update,
  loadingJwtValidationKey,
  loadingMargin,
  loadingBrand,
  setDrawdownEnabled,
  setBillingInfoFetched,
  infoLoadFailed,
} = userSlice.actions;

export const cleanUserInfo = (): AppThunk => async (dispatch) => {
  // Reset GQL-client
  graphQLClient.reset();
  // Reset CheckoutIntents client
  checkoutIntentsClient.reset();
  // Remove from LocalStorage
  localStorageApiKey.remove();
  // Remove admin account from LocalStorage
  localStorageAdminAccount.remove();
  // Remove data in Store
  dispatch(clean());
};

const getAuthProvider = (provider: AuthProvider) => {
  switch (provider) {
    case AuthProvider.Google:
      return Provider.Google;
    case AuthProvider.Local:
      return Provider.Local;
    case AuthProvider.Firebase:
      return Provider.Firebase;
    default:
      throw new Error('Unknown auth provider');
  }
};

export const fetchUserInfo =
  (uid: string, user: User, website?: string): AppThunk =>
  async (dispatch, getState) => {
    // `user` is this Firebase sign-in. Sign-out and account switches replace
    // `auth.currentUser`, and a fetch that finishes afterward must not write.
    const abandonStaleSignIn = () => {
      if (authAPI.auth.currentUser === user) {
        return false;
      }
      const stored = getState().user;
      if (stored.uid === uid && !stored.info) {
        dispatch(clean());
      }
      return true;
    };

    try {
      if (abandonStaleSignIn()) {
        return;
      }

      const state = getState();
      const noUserInfoInStore = !state.user.info || !state.user.loadingInfo;

      const claims = (await user.getIdTokenResult()).claims;
      if (abandonStaleSignIn()) {
        return;
      }

      if (noUserInfoInStore) {
        dispatch(login({ uid }));
        const token = await user.getIdToken();
        if (abandonStaleSignIn()) {
          return;
        }
        graphQLClient.set(undefined, token);

        const {
          data: {
            user: { data: userData },
          },
        } = await getUser(undefined, token);
        if (abandonStaleSignIn()) {
          return;
        }

        let userInfoModel = userData?.user;
        let billingPeriodSettings = userData?.billingPeriodSettings;
        let stripeConfig = userData?.stripeConfig;
        let amazonBusinessAccount = userData?.amazonBusinessAccount;
        let billingNotificationSettings = userData?.notificationSettings?.billingNotifications;

        const displayName = userData?.user.displayName ?? '';
        const firstName = userData?.user.displayName?.split(' ')?.at(0) ?? '';
        const lastName = userData?.user.displayName?.split(' ')?.at(1) ?? '';

        if (!user.email) {
          throw Error('User email is missing');
        }

        if (website && !userInfoModel?.website) {
          await updateAdminUser({
            updates: {
              website: website,
            },
            adminToken: token,
          });
          if (abandonStaleSignIn()) {
            return;
          }
        }

        let firstLogin = false;

        if (!userInfoModel) {
          firstLogin = true;
          const updateUser = await updateAdminUser({
            updates: {
              firstName,
              lastName,
              displayName: displayName,
              email: user.email,
              website: website ?? '',
            },

            adminToken: token,
          });
          if (abandonStaleSignIn()) {
            return;
          }
          userInfoModel = updateUser.data?.updateUser?.data?.user;
          billingPeriodSettings = updateUser.data?.updateUser?.data?.billingPeriodSettings;
          stripeConfig = updateUser.data?.updateUser?.data?.stripeConfig;
          amazonBusinessAccount = updateUser.data?.updateUser?.data?.amazonBusinessAccount;
          billingNotificationSettings =
            updateUser.data?.updateUser?.data?.notificationSettings?.billingNotifications;

          if (!userInfoModel) {
            throw Error('Could not create user');
          }
        }

        if (abandonStaleSignIn()) {
          return;
        }

        const isReadOnly = claims.readOnly ?? false;
        const userInfo: UserModel = {
          ...userInfoModel,
          uid: userInfoModel.id,
          authProvider: getAuthProvider(userInfoModel.authProvider),
          webhookURL: userInfoModel.webhookURL ?? '',
          webhookURLVerified: userInfoModel.webhookURLVerified ?? false,
          marginAmount: userInfoModel.marginAmount ?? 0,
          marginIsPercent: userInfoModel.marginIsPercent ?? false,
          jwtValidationKey: userInfoModel.jwtValidationKey ?? '',
          hmacSecretKey: userInfoModel.hmacSecretKey ?? '',
          apiKeySecondary: userInfoModel.apiKeySecondary ?? null,
          brand: userInfoModel.brand ?? null,
          stripe: stripeConfig,
          billingPeriodSettings,
          billingNotificationSettings,
          amazonBusinessAccount: amazonBusinessAccount,
          onBoardingPresentationComplete: !firstLogin,
          adminToken: token,
          isReadOnly,
          accessScopes: userData?.accessScopes ?? [],
        };

        dispatch(set({ userInfo, documentId: userInfo.uid }));
        localStorageApiKey.replace(userInfo.apiKey);

        graphQLClient.set(userInfo.apiKey, token);

        checkoutIntentsClient.set(userInfo.apiKey);

        trackHeapIdentifyEvent(userInfo);
        trackSegmentIdentifyEvent(userInfo);
      }
    } catch (error) {
      if (abandonStaleSignIn()) {
        return;
      }
      Sentry.captureException(error, {
        tags: { error: 'USER_INFO_LOAD' },
        user: { id: uid },
      });
      dispatch(infoLoadFailed());
    }
  };

export const updateJwtValidationKey =
  (jwtValidationKey: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const isLogin = state.user?.info && !state.user.loadingInfo;
    const sameVal = state.user.info?.jwtValidationKey === jwtValidationKey;
    const adminToken = state.user.info?.adminToken;

    if (!adminToken) {
      throw new Error('adminToken is undefined');
    }

    if (isLogin && !sameVal) {
      dispatch(loadingJwtValidationKey(true));
      await updateAdminUser({
        updates: { account: { jwtValidationKey } },
        adminToken,
      });
      dispatch(update({ jwtValidationKey }));
    }
  };

export const updateMarginAmount =
  (marginAmount = 0, marginIsPercent: boolean): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const isLogin = state.user.info && !state.user.loadingInfo;
    const sameVal =
      state.user.info &&
      state.user.info.marginAmount === marginAmount &&
      state.user.info.marginIsPercent === marginIsPercent;

    const adminToken = state.user.info?.adminToken;

    if (!adminToken) {
      throw new Error('adminToken is undefined');
    }

    if (isLogin && !sameVal) {
      dispatch(loadingMargin(true));
      await updateAdminUser({
        updates: { account: { margin: { amount: marginAmount, isPercent: marginIsPercent } } },
        adminToken,
      });
      dispatch(update({ marginAmount, marginIsPercent }));
    }
  };

export const updateBrand =
  (brand: BrandInput): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const isLogin = state.user.info && !state.user.loadingInfo;
    const adminToken = state.user.info?.adminToken;

    if (!adminToken) {
      throw new Error('adminToken is undefined');
    }

    if (isLogin) {
      await updateAdminBrand({
        brand,
        adminToken,
      }).catch((e) => {
        toast.error(e.message);
        dispatch(loadingBrand(false));
      });
      toast.success('Brand updated successfully');
      dispatch(update({ brand }));
    }
  };

export const onBoardingPresentationCompleted = (): AppThunk => async (dispatch) => {
  dispatch(update({ onBoardingPresentationComplete: true }));
};

export const fetchBillingInfo = (): AppThunk => async (dispatch) => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    return;
  }
  try {
    const response = await client.get<{
      drawdown: { enabled: boolean };
    }>('/api/v1/billing');
    dispatch(setDrawdownEnabled(response.drawdown.enabled));
  } catch (err) {
    // Only mark as disabled for definitive "not enabled" responses.
    // Transient errors (500, network) leave drawdownEnabled as null
    // so PrivateRoute lets the user through instead of misdirecting.
    if (err != null && typeof err === 'object' && 'status' in err) {
      const status = (err as { status: number }).status;
      if (status === 403 || status === 404) {
        dispatch(setDrawdownEnabled(false));
      }
    }
  } finally {
    dispatch(setBillingInfoFetched());
  }
};

/**
 * Selectors
 */
export const selectUserInfo = (state: RootState) => state.user.info;
export const selectOnBoardingPresentationStatus = (state: RootState) =>
  state.user.info?.onBoardingPresentationComplete;
export const selectUserInfoLoading = (state: RootState) => state.user.loadingInfo;
export const selectUserJwtValidationKeyLoading = (state: RootState) =>
  state.user.loadingInfo || state.user.loadingJwtValidationKey;
export const selectUserMarginLoading = (state: RootState) =>
  state.user.loadingInfo || state.user.loadingMargin;
export const selectUserBrandLoading = (state: RootState) =>
  state.user.loadingInfo || state.user.loadingBrand;
export const selectDrawdownEnabled = (state: RootState) => state.user.drawdownEnabled;
export const selectBillingInfoFetched = (state: RootState) => state.user.billingInfoFetched;
export const selectUserInfoLoadFailed = (state: RootState) => state.user.infoLoadFailed;

export default userSlice.reducer;
